import type { ICampaignParticipationGate } from '../types/global/campaignParticipationGate.types';
import { CAMPAIGN_PARTICIPATION_BLOCKED_CODE } from '../types/global/campaignParticipationGate.types';

export interface ICampaignWorkspaceCapacity {
    is_at_capacity?: boolean;
    filled_positions?: number;
    total_participants_count?: number;
}

export type CreatorWorkspaceParticipationSource = {
    participation_gate?: unknown;
    campaign?: { participation_gate?: unknown } | null;
    participant?: { status?: string } | null;
    campaign_capacity?: unknown;
};

const ROSTER_PARTICIPATION_STATUSES = new Set(['approved', 'in_progress', 'completed']);

const PAUSE_GATE_REASON_CODES = new Set([
    'host_manual',
    'campaign_paused',
    'payment_dispute_open',
    'payment_dispute_or_refund',
    'insufficient_campaign_funds',
    'campaign_ended',
    'campaign_closed',
    'campaign_not_published',
    'campaign_not_funded',
    'campaign_deleted',
]);

export function normalizeParticipationGate(raw: unknown): ICampaignParticipationGate | null {
    if (!raw || typeof raw !== 'object') return null;
    const g = raw as Record<string, unknown>;
    if (g.participation_allowed === undefined && g.blocked_reason_message === undefined) {
        return null;
    }
    return {
        participation_allowed: Boolean(g.participation_allowed),
        blocked_reason_code: (g.blocked_reason_code as ICampaignParticipationGate['blocked_reason_code']) ?? null,
        blocked_reason_title: typeof g.blocked_reason_title === 'string' ? g.blocked_reason_title : null,
        blocked_reason_message: typeof g.blocked_reason_message === 'string' ? g.blocked_reason_message : null,
        campaign_status_display:
            (g.campaign_status_display as ICampaignParticipationGate['campaign_status_display']) ?? 'active',
        ...(g.post_end_in_flight === true ? { post_end_in_flight: true } : {}),
        ...(typeof g.campaign_end_date === 'string' || g.campaign_end_date instanceof Date
            ? { campaign_end_date: String(g.campaign_end_date) }
            : {}),
    };
}

export function isParticipationAllowed(gate: ICampaignParticipationGate | null | undefined): boolean {
    return gate?.participation_allowed !== false;
}

/** Workspace / profile payloads may attach gate at root or on nested campaign. */
export function resolveParticipationGate(
    source:
        | {
              participation_gate?: unknown;
              campaign?: { participation_gate?: unknown } | null;
          }
        | null
        | undefined,
): ICampaignParticipationGate | null {
    if (!source) return null;
    return (
        normalizeParticipationGate(source.participation_gate) ??
        normalizeParticipationGate(source.campaign?.participation_gate)
    );
}

export function participationStatusChipLabel(gate: ICampaignParticipationGate | null | undefined): string | null {
    if (!gate || isParticipationAllowed(gate)) return null;
    const code = gate.blocked_reason_code;
    if (code === 'host_manual' || code === 'campaign_paused') return 'Paused';
    if (code === 'payment_dispute_open') return 'Paused — payment review';
    if (code === 'payment_dispute_or_refund') return 'Paused — funding issue';
    if (code === 'insufficient_campaign_funds') return 'Paused — awaiting funds';
    if (code === 'campaign_ended') return 'Ended';
    if (code === 'campaign_closed') return 'Closed';
    if (code === 'campaign_not_published') return 'Not open yet';
    if (code === 'campaign_not_funded') return 'Not funded';
    if (code === 'campaign_deleted') return 'Closed';
    if (code === 'campaign_at_capacity') return 'At capacity';
    const display = gate.campaign_status_display;
    if (display === 'paused') return 'Paused';
    if (display === 'ended') return 'Ended';
    if (display === 'closed') return 'Closed';
    if (display === 'not_funded') return 'Not funded';
    if (display === 'draft') return 'Draft';
    return gate.blocked_reason_title ?? 'Paused';
}

export const PARTICIPATION_GATE_DEFAULT_TITLE = 'Campaign paused';
export const PARTICIPATION_GATE_DEFAULT_BODY =
    'New applications and content submissions are on hold until funding is restored.';
export const PARTICIPATION_GATE_READ_ONLY_FOOTNOTE =
    'You can still read messages and review past submissions. Uploading, applying, and sending new messages are disabled until the campaign is active again.';

export const PARTICIPATION_GATE_CAPACITY_FOOTNOTE =
    'All creator spots are filled. You can review messages and past activity, but uploading and applying are disabled until a spot opens or you are approved.';

export const PARTICIPATION_GATE_CAPACITY_INFO =
    "This campaign is full — new creators can't apply. You can continue uploading and messaging.";

export function isRosterParticipantStatus(status: string | null | undefined): boolean {
    return status != null && ROSTER_PARTICIPATION_STATUSES.has(status);
}

export function normalizeCampaignCapacity(raw: unknown): ICampaignWorkspaceCapacity | null {
    if (!raw || typeof raw !== 'object') return null;
    const c = raw as Record<string, unknown>;
    if (c.is_at_capacity !== true && c.filled_positions === undefined && c.total_participants_count === undefined) {
        return null;
    }
    return {
        is_at_capacity: c.is_at_capacity === true,
        filled_positions: typeof c.filled_positions === 'number' ? c.filled_positions : undefined,
        total_participants_count:
            typeof c.total_participants_count === 'number' ? c.total_participants_count : undefined,
    };
}

export function resolveCampaignCapacity(
    source: { campaign_capacity?: unknown } | null | undefined,
): ICampaignWorkspaceCapacity | null {
    return normalizeCampaignCapacity(source?.campaign_capacity);
}

/** Creator workspace — honors API gate; ignores stale capacity blocks for roster participants. */
export function isCreatorWorkspaceParticipationBlocked(
    workspace: CreatorWorkspaceParticipationSource | null | undefined,
): boolean {
    if (!workspace) return false;
    const gate = resolveParticipationGate(workspace);
    if (isParticipationAllowed(gate)) return false;
    if (isCampaignAtCapacityGate(gate) && isRosterParticipantStatus(workspace.participant?.status)) {
        return false;
    }
    return true;
}

export function shouldShowCampaignCapacityInfo(
    workspace: CreatorWorkspaceParticipationSource | null | undefined,
): boolean {
    const capacity = resolveCampaignCapacity(workspace);
    if (!capacity?.is_at_capacity) return false;
    return isRosterParticipantStatus(workspace?.participant?.status);
}

function participationGateFootnote(gate: ICampaignParticipationGate): string | null {
    if (isCampaignAtCapacityGate(gate)) return PARTICIPATION_GATE_CAPACITY_FOOTNOTE;
    const code = gate.blocked_reason_code;
    if (code && PAUSE_GATE_REASON_CODES.has(code)) return PARTICIPATION_GATE_READ_ONLY_FOOTNOTE;
    if (gate.campaign_status_display === 'paused' || gate.campaign_status_display === 'ended') {
        return PARTICIPATION_GATE_READ_ONLY_FOOTNOTE;
    }
    return null;
}

/** Title, chip, and body copy shown in creator workspace participation banners. */
export function getParticipationGateBannerCopy(gate: ICampaignParticipationGate): {
    chipLabel: string | null;
    title: string;
    body: string;
    footnote: string | null;
} {
    return {
        chipLabel: participationStatusChipLabel(gate),
        title: gate.blocked_reason_title?.trim() || PARTICIPATION_GATE_DEFAULT_TITLE,
        body: gate.blocked_reason_message?.trim() || PARTICIPATION_GATE_DEFAULT_BODY,
        footnote: participationGateFootnote(gate),
    };
}

/** Lock message when uploads / live URLs are disabled in creator workspace panels. */
export function getParticipationGateActionLockMessage(
    gate: ICampaignParticipationGate | null | undefined,
    context: 'upload' | 'live_url' = 'upload',
): string {
    if (!gate || isParticipationAllowed(gate)) return '';
    const code = gate.blocked_reason_code;
    if (code === 'campaign_at_capacity') {
        return context === 'live_url'
            ? 'Live URL submission is disabled until you are approved onto the roster.'
            : 'Uploads are disabled until you are approved onto the roster — this campaign is full.';
    }
    if (code === 'campaign_ended') {
        return context === 'live_url'
            ? 'This campaign has ended. Live URL submission is no longer available.'
            : 'This campaign has ended. Uploads are no longer accepted.';
    }
    if (
        code === 'insufficient_campaign_funds' ||
        code === 'payment_dispute_open' ||
        code === 'payment_dispute_or_refund'
    ) {
        return context === 'live_url'
            ? 'Marking as shared and submitting live URLs are paused while campaign funding is restored.'
            : 'Uploads are paused while campaign funding is restored.';
    }
    if (code === 'host_manual' || code === 'campaign_paused') {
        const detail = gate.blocked_reason_message?.trim();
        return (
            detail ||
            (context === 'live_url'
                ? 'Marking as shared and submitting live URLs are paused while the campaign is on hold.'
                : 'Uploads are paused while the campaign is on hold.')
        );
    }
    const fallback = gate.blocked_reason_message?.trim() || PARTICIPATION_GATE_DEFAULT_BODY;
    return context === 'live_url' ? 'Marking as shared and submitting live URLs are paused.' : fallback;
}

export function extractParticipationBlockedFromError(error: unknown): {
    message: string;
    gate: ICampaignParticipationGate | null;
} | null {
    const err = error as {
        data?: {
            message?: string;
            details?: {
                code?: string;
                blocked_reason_code?: string;
                participation_gate?: unknown;
            };
        };
        message?: string;
        details?: { code?: string; participation_gate?: unknown; message?: string };
    };
    const details = err?.data?.details ?? err?.details;
    if (details?.code !== CAMPAIGN_PARTICIPATION_BLOCKED_CODE) return null;
    const gate = normalizeParticipationGate(details.participation_gate);
    const message =
        err?.data?.message ??
        gate?.blocked_reason_message ??
        'This campaign is paused. New applications and content submissions are on hold.';
    return { message, gate };
}

export function isCampaignAtCapacityGate(gate: ICampaignParticipationGate | null | undefined): boolean {
    return gate?.blocked_reason_code === 'campaign_at_capacity';
}

export type PublicCampaignParticipationSource = {
    at_capacity?: boolean;
    is_at_capacity?: boolean;
    filling_fast?: boolean;
    participation_gate?: unknown;
    campaign?: { participation_gate?: unknown; at_capacity?: boolean; is_at_capacity?: boolean } | null;
};

function readPublicAtCapacity(source: PublicCampaignParticipationSource | null | undefined): boolean {
    if (!source) return false;
    if (source.at_capacity === true || source.is_at_capacity === true) return true;
    return isCampaignAtCapacityGate(resolveParticipationGate(source));
}

export function isPublicCampaignFillingFast(source: PublicCampaignParticipationSource | null | undefined): boolean {
    return source?.filling_fast === true;
}

/** Public detail — Apply enabled when gate allows and campaign is not at capacity. */
export function isPublicCampaignApplyEnabled(source: PublicCampaignParticipationSource | null | undefined): boolean {
    if (readPublicAtCapacity(source)) return false;
    const gate = resolveParticipationGate(source);
    if (!gate) return true;
    return isParticipationAllowed(gate);
}

/** Public campaign detail — disable apply from API flags (no client-side slot math). */
export function isPublicCampaignParticipationBlocked(
    source: PublicCampaignParticipationSource | null | undefined,
): boolean {
    return !isPublicCampaignApplyEnabled(source);
}

export function getPublicCampaignApplyLabel(
    source: PublicCampaignParticipationSource | null | undefined,
    fallback = 'Apply Now',
): string {
    if (isPublicCampaignApplyEnabled(source)) return fallback;
    return 'Campaign at capacity';
}

export function getPublicCampaignRoleSubtitle(
    source: PublicCampaignParticipationSource | null | undefined,
): string | null {
    if (isPublicCampaignApplyEnabled(source)) return null;
    return 'Campaign at capacity';
}
